import CT from '@marketplace-ai/tags';
import React from 'react';
import { ActorRefFrom } from 'xstate';
import { WizardStepsProps } from '~/components/Wizard';
import { hasValidCostCalculation } from '~/features/market/listings/components/costCalculatorMoney';
import { MsaasDetail } from '~/types/hlx/msaasDetail';
import type { ListingSide } from '../../b2bSideCopy';
import { CostCalculator } from '../components/CostCalculator';
import { AdditionalTermsForm } from '../shared-steps/additional-terms/AdditionalTermsForm';
import { FacilityForm } from '../shared-steps/facilities/FacilityForm';
import { msaasListingsMachine } from './msaasListing.machine';
import { BasicInfoForm } from './wizard-steps/basic-info/BasicInfoForm';
import { CompensationForm } from './wizard-steps/compensation/CompensationForm';
import { LaborMetricsForm } from './wizard-steps/labor-metrics/LaborMetricsForm';
import { CoverageForm } from './wizard-steps/offering-coverage/CoverageForm';
import { ReviewInfo } from './wizard-steps/review-info/ReviewInfo';
import { ShiftDetailsForm } from './wizard-steps/shift-details/ShiftDetailsForm';

const TAGS = CT.employer.msaasListings.wizard;

export const createWizardSteps = (
  actor: ActorRefFrom<typeof msaasListingsMachine>,
  detail: MsaasDetail | undefined,
  loading: boolean,
  // [MCV-1889] A selling listing has no facilities: its step 2 is the coverage step.
  side: ListingSide = 'SEEKING'
): WizardStepsProps[] => {
  const DEFAULT_PRIMARY_BTN = {
    label: 'Next',
    cyTag: TAGS.primaryBtn,
    isSubmit: true,
  };
  const DEFAULT_BACK_BTN = {
    label: 'Back',
    cyTag: TAGS.backBtn,
    onClick: () => {
      actor.send({ type: 'ON_PREV' });
    },
  };
  const SKIP_BTN = {
    label: 'Skip',
    cyTag: TAGS.secondaryBtn,
    onClick: () => {
      actor.send({ type: 'ON_SKIP' });
    },
  };
  return [
    {
      header: 'Step 1: Basic Listing Information',
      label: 'Basic Info',
      cyTag: TAGS.basicInfoStep,
      children: ({ footer }) => React.createElement(BasicInfoForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: () => {
            actor.send({ type: 'ON_PREV' });
          },
        },
      },
    },
    side === 'OFFERING'
      ? {
          header: 'Step 2: Coverage & Providers',
          label: 'Coverage',
          cyTag: 'msaas-listing-coverage-step',
          children: ({ footer }) => React.createElement(CoverageForm, { footer }),
          footer: {
            primaryButton: DEFAULT_PRIMARY_BTN,
            backButton: DEFAULT_BACK_BTN,
          },
        }
      : {
          header: 'Step 2: Facility Information',
          label: 'Facilities',
          cyTag: TAGS.facilitiesStep,
          children: ({ footer }) => React.createElement(FacilityForm, { footer, actor }),
          footer: {
            primaryButton: DEFAULT_PRIMARY_BTN,
            backButton: DEFAULT_BACK_BTN,
          },
        },
    {
      header: 'Step 3: Labor Metrics & Duration',
      label: 'Labor Metrics',
      cyTag: TAGS.laborMetricsStep,
      children: ({ footer }) => React.createElement(LaborMetricsForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 4: Base Compensation Structure',
      label: 'Compensation',
      cyTag: TAGS.compensationStep,
      children: ({ footer }) => React.createElement(CompensationForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 5: Shift Details (Optional)',
      label: 'Shift Details',
      cyTag: TAGS.shiftDetailsStep,
      children: ({ footer }) => React.createElement(ShiftDetailsForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
        secondaryButton: SKIP_BTN,
      },
    },
    {
      header: 'Step 6: Cost Calculator',
      label: 'Cost Calculator',
      cyTag: TAGS.costCalculatorStep,
      children: ({ footer }) =>
        React.createElement(CostCalculator, { footer, detail, loading, side }),
      footer: {
        primaryButton: {
          ...DEFAULT_PRIMARY_BTN,
          isSubmit: false,
          onClick: () => {
            actor.send({ type: 'ON_NEXT' });
          },
        },
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 7: Additional Terms (Optional)',
      label: 'Additional Terms',
      cyTag: TAGS.additionalInfoStep,
      children: ({ footer }) => React.createElement(AdditionalTermsForm, { footer, actor }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
        secondaryButton: SKIP_BTN,
      },
    },
    {
      header: 'Step 8: Review & Submit',
      label: 'Review',
      cyTag: TAGS.reviewStep,
      children: ({ footer }) => React.createElement(ReviewInfo, { footer }),
      footer: {
        primaryButton: {
          label: 'Submit',
          cyTag: CT.employer.msaasListings.wizard.primaryBtn,
          disabled: !hasValidCostCalculation(detail),
          onClick: () => {
            actor.send({ type: 'ON_SUBMIT_LISTING' });
          },
        },
        secondaryButton: {
          label: 'Save as Draft',
          cyTag: TAGS.secondaryBtn,
          onClick: () => {
            actor.send({ type: 'ON_SAVE_DRAFT' });
          },
        },
        backButton: {
          label: 'Back to Edit',
          cyTag: TAGS.backBtn,
          onClick: () => {
            actor.send({ type: 'ON_PREV' });
          },
        },
      },
    },
  ];
};
